class MegaMenuComponent extends HTMLElement {
  constructor() {
    super();
    /** @type {NodeListOf<HTMLElement>} */
    this.mediaItems = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-submenu-media]'));

    /** @type {HTMLAnchorElement | null} */
    this.activeLink = null;

    /** @type {HTMLElement | null} */
    this.activeSubmenu = null;

    /** @type {HTMLElement | null} */
    this.activeMedia = null;

    /** @type {number | null} */
    this.pendingVideoPlayTimeoutId = null;

    /** @type {((event: TransitionEvent) => void) | null} */
    this.panelTransitionEndHandler = null;

    /** @type {number | null} */
    this.panelFallbackTimeoutId = null;

    /** @type {ResizeObserver | null} */
    this.resizeObserver = null;

    this.onClick = this.onClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onLinkHover = this.onLinkHover.bind(this);
    this.onLinkLeave = this.onLinkLeave.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.addEventListener('click', this.onClick);
  }

  get isDesktop() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  get isHoverDesktop() {
    return window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)').matches;
  }

  connectedCallback() {
    this.setPanelMaxHeightToContent();

    // On observe le composant lui-même (le contenu) pour détecter les changements de taille
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this);

    // On observe aussi le panel pour détecter les changements de padding/border
    const panel = this.getPanelElement();
    if (panel) {
      this.resizeObserver.observe(panel);
    }

    this.addEventListener('mouseover', this.onLinkHover);
    this.addEventListener('mouseout', this.onLinkLeave);
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('mouseover', this.onLinkHover);
    this.removeEventListener('mouseout', this.onLinkLeave);
    this.removeEventListener('keydown', this.onKeydown);
    this.#clearPendingVideoPlay();
    this.#clearPanelTransitionListeners();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    if (event.code === 'Escape') {
      if (this.activeLink) {
        this.close();
        this.activeLink?.focus();
        event.stopPropagation();
      }
    }
  }

  onResize() {
    requestAnimationFrame(() => {
      if (!this.isDesktop) {
        this.style.removeProperty('--mega-menu-submenu-height');
      }
      this.setPanelMaxHeightToContent();
    });
  }

  /** @param {MouseEvent} event */
  onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const backBtn = target.closest('.mega-menu__back-btn');
    if (backBtn) {
      event.preventDefault();
      this.close();
      return;
    }

    const clickedLink = target.closest('.mega-menu__menu-link.has-submenu');
    if (!(clickedLink instanceof HTMLAnchorElement)) return;

    // Only handle links that belong to this component
    if (!this.contains(clickedLink)) return;

    event.preventDefault();

    const submenuId = clickedLink.dataset.submenuId;
    if (!submenuId) return;

    this.activateSubmenu(submenuId, clickedLink);
  }

  /**
   * Activates a submenu panel and its associated media.
   * Shared by hover (desktop) and click (fallback / mobile).
   * @param {string} submenuId
   * @param {HTMLAnchorElement} clickedLink
   */
  activateSubmenu(submenuId, clickedLink) {
    /** @type {HTMLElement | null} */
    const targetSubmenu = /** @type {HTMLElement | null} */ (
      this.querySelector(`[data-submenu="${submenuId}"]`)
    );

    if (this.activeLink === clickedLink) return;

    this.animatePanelHeight(() => {
      this.clearActiveInternal();

      if (!targetSubmenu) return;

      clickedLink.classList.add('active');
      clickedLink.setAttribute('aria-expanded', 'true');
      targetSubmenu.classList.add('active');
      this.activeLink = clickedLink;
      this.activeSubmenu = targetSubmenu;

      this.setSubmenuHeight(targetSubmenu);
      this.setActiveMedia(submenuId);
    });
  }

  clearActiveInternal() {
    if (this.activeLink) {
      this.activeLink.classList.remove('active');
      this.activeLink.setAttribute('aria-expanded', 'false');
      this.activeLink = null;
    }

    if (this.activeSubmenu) {
      this.activeSubmenu.classList.remove('active');
      this.activeSubmenu = null;
    }

    this.clearSubmenuHeight();
    this.clearActiveMedia();
  }

  getPanelElement() {
    /** @type {HTMLElement | null} */
    const panel = /** @type {HTMLElement | null} */ (this.closest('.header__menu--mega-menu'));
    return panel;
  }

  calculateTotalHeight() {
    const panel = this.getPanelElement();
    if (!panel) return 0;

    let contentHeight = this.scrollHeight;
    const activeSubmenu = this.querySelector('.mega-menu__submenu-list.active');
    if (activeSubmenu) {
      contentHeight = Math.max(contentHeight, activeSubmenu.scrollHeight);
    }

    const panelStyle = window.getComputedStyle(panel);
    const verticalPadding = (parseFloat(panelStyle.paddingTop) || 0) + (parseFloat(panelStyle.paddingBottom) || 0);
    const verticalBorder = (parseFloat(panelStyle.borderTopWidth) || 0) + (parseFloat(panelStyle.borderBottomWidth) || 0);

    return contentHeight + verticalPadding + verticalBorder + 1;
  }

  setPanelMaxHeightToContent() {
    const panel = this.getPanelElement();
    if (!panel) return;

    const totalHeight = this.calculateTotalHeight();
    panel.style.setProperty('--mega-menu-panel-max-height', `${totalHeight}px`);
  }

  /** @param {() => void} stateChange */
  animatePanelHeight(stateChange) {
    if (!this.isDesktop) {
      stateChange();
      this.setPanelMaxHeightToContent();
      return;
    }

    const panel = this.getPanelElement();
    if (!panel) {
      stateChange();
      return;
    }

    this.#clearPanelTransitionListeners(panel);

    const startHeight = panel.getBoundingClientRect().height;
    panel.style.setProperty('--mega-menu-panel-max-height', `${startHeight}px`);
    panel.getBoundingClientRect();

    stateChange();

    requestAnimationFrame(() => {
      // Pour l'animation, on utilise scrollHeight du panel qui devrait être correct maintenant
      // ou on recalcule explicitement si besoin, mais scrollHeight est standard pour les transitions
      const endHeight = this.calculateTotalHeight();
      panel.style.setProperty('--mega-menu-panel-max-height', `${endHeight}px`);

      /** @param {TransitionEvent} event */
      this.panelTransitionEndHandler = event => {
        if (event.target !== panel) return;
        if (event.propertyName !== 'max-height') return;

        this.#clearPanelTransitionListeners(panel);
        this.setPanelMaxHeightToContent();
      };

      panel.addEventListener('transitionend', this.panelTransitionEndHandler);

      this.panelFallbackTimeoutId = window.setTimeout(() => {
        this.panelFallbackTimeoutId = null;
        this.setPanelMaxHeightToContent();
      }, 500);
    });
  }

  /** @param {HTMLElement} submenuElement */
  setSubmenuHeight(submenuElement) {
    if (!this.isDesktop) return;
    if (!submenuElement) return;
    this.style.setProperty('--mega-menu-submenu-height', `${submenuElement.scrollHeight}px`);
  }

  clearSubmenuHeight() {
    if (!this.isDesktop) return;
    this.style.setProperty('--mega-menu-submenu-height', '0px');
  }

  /** @param {MouseEvent} event */
  onLinkHover(event) {
    if (!this.isDesktop) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    // On hover-capable desktop, hovering a parent link activates its submenu (sticky-hover)
    if (this.isHoverDesktop) {
      const submenuLink = target.closest('.mega-menu__menu-link.has-submenu');
      if (submenuLink instanceof HTMLAnchorElement && this.contains(submenuLink)) {
        const submenuId = submenuLink.dataset.submenuId;
        if (submenuId) {
          this.activateSubmenu(submenuId, submenuLink);
        }
      }
    }

    const link = target.closest('[data-media-target]');
    if (!link || !(link instanceof HTMLElement)) return;

    const mediaTarget = link.dataset.mediaTarget;
    if (mediaTarget) {
      this.activateSpecificMedia(mediaTarget);
    }
  }

  /** @param {MouseEvent} event */
  onLinkLeave(event) {
    if (!this.isDesktop) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('[data-media-target]');
    if (!link || !(link instanceof HTMLElement)) return;

    if (event.relatedTarget instanceof Node && link.contains(event.relatedTarget)) {
      return;
    }

    // If the link belongs to a submenu list, revert to that submenu's default media
    const submenuList = link.closest('.mega-menu__submenu-list');
    if (submenuList instanceof HTMLElement && submenuList.dataset.submenu) {
      this.setActiveMedia(submenuList.dataset.submenu);
      return;
    }

    // For list links (by_stone, by_shape, by_style), revert to active submenu default or clear
    if (this.activeSubmenu && this.activeSubmenu.dataset.submenu) {
      this.setActiveMedia(this.activeSubmenu.dataset.submenu);
    } else {
      this.clearActiveMedia();
    }
  }

  /** @param {string} mediaId */
  activateSpecificMedia(mediaId) {
    if (!this.isDesktop) return;

    if (!this.mediaItems?.length) return;

    const targetMedia = Array.from(this.mediaItems).find(item => item.dataset.mediaId === mediaId);
    if (!targetMedia || targetMedia.classList.contains('active')) return;

    this.#clearPendingVideoPlay();

    let nextActiveVideo = null;

    // Deactivate all media globally, activate only the target
    for (const item of this.mediaItems) {
      const isActive = item === targetMedia;
      item.classList.toggle('active', isActive);

      const video = item.querySelector('video');
      if (video) {
        isActive ? (nextActiveVideo = video) : video.pause?.();
      }
    }

    this.activeMedia = targetMedia;
    this.classList.add('mega-menu--media-open');

    if (nextActiveVideo) {
      this.pendingVideoPlayTimeoutId = window.setTimeout(() => {
        if (!targetMedia.classList.contains('active')) return;
        nextActiveVideo.play?.();
      }, 250);
    }
  }

  /** @param {string} submenuId */
  setActiveMedia(submenuId) {
    if (!this.isDesktop) return;

    if (!this.mediaItems?.length) return;

    this.#clearPendingVideoPlay();

    /** @type {HTMLElement | null} */
    let nextActiveMedia = null;
    /** @type {HTMLVideoElement | null} */
    let nextActiveVideo = null;

    for (const item of this.mediaItems) {
      const isSubmenuMatch = item.dataset.submenuMedia === submenuId;
      const isActive = isSubmenuMatch && item.hasAttribute('data-is-default');
      item.classList.toggle('active', isActive);

      const video = item.querySelector('video');
      if (video) {
        isActive ? (nextActiveVideo = video) : video.pause?.();
      }

      if (isActive) {
        nextActiveMedia = item;
      }
    }

    this.activeMedia = nextActiveMedia;
    this.classList.toggle('mega-menu--media-open', Boolean(this.activeMedia));

    if (nextActiveMedia && nextActiveVideo) {
      this.pendingVideoPlayTimeoutId = window.setTimeout(() => {
        if (!nextActiveMedia.classList.contains('active')) return;
        nextActiveVideo.play?.();
      }, 250);
    }
  }

  clearActiveMedia() {
    if (!this.mediaItems?.length) return;

    this.#clearPendingVideoPlay();

    for (const item of this.mediaItems) {
      item.classList.remove('active');
      item.querySelector('video')?.pause?.();
    }

    this.activeMedia = null;
    this.classList.remove('mega-menu--media-open');
  }

  #clearPendingVideoPlay() {
    if (!this.pendingVideoPlayTimeoutId) return;
    window.clearTimeout(this.pendingVideoPlayTimeoutId);
    this.pendingVideoPlayTimeoutId = null;
  }

  /** @param {HTMLElement | null} [panel] */
  #clearPanelTransitionListeners(panel) {
    const panelElement = panel || this.getPanelElement();
    if (!panelElement) return;

    const previousTransitionEndHandler = this.panelTransitionEndHandler;
    if (previousTransitionEndHandler) {
      panelElement.removeEventListener('transitionend', previousTransitionEndHandler);
      this.panelTransitionEndHandler = null;
    }

    if (this.panelFallbackTimeoutId) {
      window.clearTimeout(this.panelFallbackTimeoutId);
      this.panelFallbackTimeoutId = null;
    }
  }

  // Fermer le menu depuis l'extérieur si nécessaire
  /** @param {{ immediate?: boolean }=} options */
  close(options = {}) {
    const immediate = Boolean(options.immediate);

    if (immediate) {
      this.#clearPendingVideoPlay();
      this.#clearPanelTransitionListeners();
      this.clearActiveInternal();
      this.setPanelMaxHeightToContent();
      return;
    }

    this.animatePanelHeight(() => {
      this.clearActiveInternal();
    });
  }
}

if (!customElements.get('mega-menu-component')) {
  customElements.define('mega-menu-component', MegaMenuComponent);
}
